import { useState, useCallback } from "react";
import { IDENTITY_SERVICE_BASE_URL, ACTOR_ID_HEADER_KEY } from "constants";
import apiClient from "utils/apiClient";
import { useAuth } from "contexts/AuthContext";

const API_BASE_URL = `${IDENTITY_SERVICE_BASE_URL}/api/v1/user-deletion-requests`;
const CORRELATION_ID_HEADER_KEY = "O-CORRELATION-ID";

const useUserDeletionRequests = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const request = useCallback(async (method, url, data = undefined, config = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.request({
        method,
        url,
        data,
        ...config,
      });
      return response.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.response?.data?.error || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const createUserDeletionRequest = useCallback(
    async ({ deletionReason, actorId, correlationId }) => {
      const headers = {
        [ACTOR_ID_HEADER_KEY]: actorId || user?.id || "",
      };

      if (correlationId) {
        headers[CORRELATION_ID_HEADER_KEY] = correlationId;
      }

      return request("post", API_BASE_URL, { deletionReason }, { headers });
    },
    [request, user?.id],
  );

  const getUserDeletionRequestById = useCallback(
    async (id) => request("get", `${API_BASE_URL}/${id}`),
    [request],
  );

  const getAllUserDeletionRequests = useCallback(
    async ({
      userId,
      status,
      page = 0,
      size = 20,
      sortBy = "requestedAt",
      direction = "DESC",
    } = {}) => {
      const params = new URLSearchParams({
        page: String(page),
        size: String(size),
        sortBy,
        direction,
      });

      if (userId) {
        params.append("userId", userId);
      }
      if (status) {
        params.append("status", status);
      }

      return request("get", `${API_BASE_URL}?${params.toString()}`);
    },
    [request],
  );

  const getLatestUserDeletionRequestByUser = useCallback(
    async (userId) => request("get", `${API_BASE_URL}/user/${userId}/latest`),
    [request],
  );

  const updateUserDeletionRequestStatus = useCallback(
    async (id, { status, failureReason } = {}) =>
      request("patch", `${API_BASE_URL}/${id}/status`, {
        status,
        failureReason,
      }),
    [request],
  );

  const scheduleUserDeletion = useCallback(
    async (id, deletionScheduledAt) =>
      request("patch", `${API_BASE_URL}/${id}/schedule`, {
        deletionScheduledAt,
      }),
    [request],
  );

  const markUserDeletionFailed = useCallback(
    async (id, failureReason) =>
      request("patch", `${API_BASE_URL}/${id}/fail`, { failureReason }),
    [request],
  );

  const markUserDeletionDeleted = useCallback(
    async (id) => request("patch", `${API_BASE_URL}/${id}/deleted`),
    [request],
  );

  const getApprovedUserDeletionRequestsForJob = useCallback(
    async () => request("get", `${API_BASE_URL}/approved/job`),
    [request],
  );

  const deleteUserDeletionRequest = useCallback(
    async (id) => request("delete", `${API_BASE_URL}/${id}`),
    [request],
  );

  return {
    loading,
    error,
    createUserDeletionRequest,
    getUserDeletionRequestById,
    getAllUserDeletionRequests,
    getLatestUserDeletionRequestByUser,
    updateUserDeletionRequestStatus,
    scheduleUserDeletion,
    markUserDeletionFailed,
    markUserDeletionDeleted,
    getApprovedUserDeletionRequestsForJob,
    deleteUserDeletionRequest,
  };
};

export default useUserDeletionRequests;
